import { useEffect } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useInfiniteQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Button, EmptyState, NotificationRow, PageTitle, Spinner } from '../components/ui'
import { api, call } from '../lib/api'
import { requireUser } from '../lib/auth'
import { meQuery, unreadQuery } from '../lib/queries'
import { ago } from '../lib/time'

// The notification center (designs/20261020-notification-center.md §6): a
// person-level page like /account, newest first, 30 a page (the server keeps
// 50, §5). Opening it clears the badge (Q2): the newest row it shows is
// posted to /notifications/read once the list is in.
const page = 30

export const Route = createFileRoute('/notifications')({
  beforeLoad: ({ context, location }) => requireUser(context.queryClient, location.href),
  component: Notifications,
})

function Notifications() {
  const { data: me } = useSuspenseQuery(meQuery)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const list = useInfiniteQuery({
    queryKey: ['notifications', 'list'],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      call(api.GET('/notifications', { params: { query: { limit: page, ...(pageParam ? { before: pageParam } : {}) } } })),
    getNextPageParam: (last) => {
      const rows = last.notifications ?? []
      return rows.length === page ? rows[rows.length - 1].id : undefined
    },
    staleTime: 0,
  })
  const rows = list.data?.pages.flatMap((p) => p.notifications ?? []) ?? []
  const newest = list.data?.pages[0]?.notifications?.[0]
  const newestUnread = newest && !newest.read_at ? newest.id : undefined

  useEffect(() => {
    if (newestUnread === undefined) return
    call(api.POST('/notifications/read', { body: { before: newestUnread } }))
      .then(() => qc.setQueryData(unreadQuery.queryKey, (d) => (d ? { ...d, count: 0 } : d)))
      .catch(() => undefined)
  }, [newestUnread, qc])

  const home = me.leagues?.[0]
  return (
    <div className="mx-auto grid w-full max-w-[560px] content-start gap-4 px-4 pb-16 pt-[calc(env(safe-area-inset-top,0px)+20px)]">
      <div className="flex items-center justify-between gap-3">
        {home ? (
          <Link to="/l/$slug" params={{ slug: home.slug }} search={{ category: undefined }} className="text-[14px] font-semibold text-muted">
            ← {home.name}
          </Link>
        ) : (
          <span />
        )}
      </div>
      <PageTitle>Notifications</PageTitle>
      {list.isPending ? (
        <div className="grid place-items-center py-10">
          <Spinner track />
        </div>
      ) : list.isError ? (
        <EmptyState title="Couldn't load notifications" action={<Button size="sm" onClick={() => list.refetch()}>Try again</Button>} />
      ) : rows.length === 0 ? (
        <EmptyState title="Nothing yet">When someone comments on your cat, it shows up here.</EmptyState>
      ) : (
        <ul className="grid gap-2">
          {rows.map((n) => (
            <li key={n.id}>
              <NotificationRow
                faces={(n.actors ?? []).map((a) => a.avatar_url)}
                text={n.text}
                when={ago(n.updated_at)}
                unread={!n.read_at}
                onOpen={() => navigate({ href: n.url })}
              />
            </li>
          ))}
        </ul>
      )}
      {list.hasNextPage && (
        <Button size="sm" className="justify-self-center" busy={list.isFetchingNextPage} onClick={() => list.fetchNextPage()}>
          Show older
        </Button>
      )}
    </div>
  )
}
